import { Link } from '@tanstack/react-router';
import { DocsLayout } from '../../components/docs/DocsLayout.tsx';
import { MediaImage } from '../../components/MediaImage.tsx';
import { useDocumentTitle } from '../../hooks/useDocumentTitle.ts';
import { useT } from '../../i18n.ts';

export function DocsDocuments() {
  const t = useT();
  useDocumentTitle(t('docs.documents.title'));

  return (
    <DocsLayout
      path="/docs/documents"
      title={t('docs.documents.label')}
      lede="A document is a structured list of lists that holds your notes, context, reference materials, requirements, and decisions, along with links and images, organized with sections and labels. This page covers creating documents, sharing them, and managing who can see and change them."
    >
      <h2 id="intro">About documents</h2>
      <p>
        A <strong>document</strong> is a structured list of lists that
        holds your notes, context, reference materials, requirements, and
        decisions, along with links and images, organized with sections and
        labels. It describes one goal, such as a room to renovate, a product to
        ship, or an event to plan, and it holds everything about that goal:
      </p>
      <ul>
        <li>
          the <Link to="/docs/details">content</Link> of the document, grouped
          into sections;
        </li>
        <li>
          the <Link to="/docs/teams">teams</Link> who participate in it;
        </li>
        <li>
          an <Link to="/docs/approval-rules">approval rule</Link> that
          governs how changes get approved;
        </li>
        <li>a complete history of every change ever approved.</li>
      </ul>
      <p>
        A document doesn’t stand still. It moves forward one approved change at a
        time, and each version builds on the one before it, so you can always see
        both what the document says now and how it got there.
      </p>

      {/* The settings-page screenshot was captured and pushed under the
          `projects-software` stem; the next `marketing:asset:push` of the
          `documents` target replaces it with `documents-software`. */}
      <MediaImage
        base="projects-software"
        label="The document settings page, where you set the document’s approval rule, manage its teams, and delete the document."
      />

      <h2 id="create">Create a document</h2>
      <p>To start a new document:</p>
      <ol>
        <li>
          Open <strong>Documents</strong> from the sidebar and choose{' '}
          <strong>New document</strong>.
        </li>
        <li>Give it a clear name. This is how it appears in your document list.</li>
        <li>Create it. The document opens empty, ready for its first section.</li>
      </ol>
      <p>
        A new document starts with no required approvals, so your early changes take
        effect as soon as you submit them. That’s the fastest way to get your first
        structure down. When you’re ready to collaborate, add a team and set up
        approvals.
      </p>

      <h2 id="delete">Delete a document</h2>
      <p>
        Deleting a document removes the document and its entire history, including
        all of its contents and every version. This can’t be undone, so Goalpost
        asks you to confirm.
      </p>
      <ol>
        <li>
          Go to the document and select <strong>Settings</strong> from the document
          nav.
        </li>
        <li>
          In the <strong>Delete Document</strong> section, choose{' '}
          <strong>Delete Document</strong> and confirm.
        </li>
      </ol>
      <p>
        Only a workspace owner or admin can delete a document. If you only want to
        stop working on something, consider removing your team from it instead of
        deleting it outright, which preserves the record for anyone who still needs
        it.
      </p>

      <h2 id="permissions">Document permissions</h2>
      <p>
        What someone can do in a document depends on the teams they’re on and their
        role in the workspace. In broad terms:
      </p>
      <ul>
        <li>
          <strong>Members of a participating team</strong> can view the whole
          document and its history, and can create drafts to propose changes.
        </li>
        <li>
          <strong>Approvers</strong> (members of a team named in the document’s
          approval rule) can additionally sign off on submitted changes.
        </li>
        <li>
          <strong>Workspace owners and admins</strong> manage the document itself:
          adding and removing teams, setting the approval rule, and deleting
          the document.
        </li>
      </ul>
      <p>
        Goalpost favors coordination over compartmentalization: when a team is on a
        document, its members see the whole document, not a sliver of it. If
        someone should only see part of the work, that’s a sign it should be a
        separate document.
      </p>

      <h2 id="approvals">Approvals</h2>
      <p>
        Each document has an <strong>approval rule</strong> that decides whether
        changes go through automatically or wait for sign-off, and from whom. New
        documents start with no approvals required, and you can tighten this at any
        time, for example by requiring a client team and your own team to both
        approve.
      </p>
      <p>
        Setting this up, the way rules are built, and how approvals combine are
        covered in full under{' '}
        <Link to="/docs/approval-rules">Approval Rules</Link>.
      </p>

      <h2 id="membership">Document membership</h2>
      <p>
        Membership in a document is <strong>team-based</strong>. You don’t add
        individuals to a document directly. You add a <strong>team</strong>, and
        every member of that team gains access. This is deliberate: it keeps access
        easy to reason about (this team is on this document) and easy to change (add
        or remove the whole team at once).
      </p>
      <p>
        Being on a participating team is <strong>required for viewing</strong> a
        document. If you can’t see a document you expect to, it’s because none of your
        teams have been added to it, so ask a workspace admin to add the right team.
        Teams are added from the team’s side; see{' '}
        <Link to="/docs/teams" hash="add-to-document">
          Add a team to a document
        </Link>
        .
      </p>
    </DocsLayout>
  );
}
